import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getAddonsFlat, getAddonGroups, getAddonTypeLabel } from '../utils/addons';

const ItemCustomizationModal = ({ item, isOpen, onClose, onConfirm, editMode = false, initialItem = null, onUpdate = null }) => {
  const [selectedSizeId, setSelectedSizeId] = useState(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const [selectedAddonCategory, setSelectedAddonCategory] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const sizes = useMemo(() => item?.sizes || [], [item?.sizes]);
  const addonGroups = useMemo(() => getAddonGroups(item?.addons || []), [item?.addons]);
  const addons = useMemo(() => getAddonsFlat(item?.addons || []), [item?.addons]);
  const currentCategoryPositions = useMemo(() => {
    if (!selectedAddonCategory || addonGroups.length === 0) return [];
    const group = addonGroups.find((g) => g.type === selectedAddonCategory);
    return group ? group.positions : [];
  }, [addonGroups, selectedAddonCategory]);

  // Инициализация при открытии модалки
  useEffect(() => {
    if (isOpen && item) {
      if (editMode && initialItem) {
        // Режим редактирования: предзаполняем текущими значениями
        
        // Отладка: выводим данные для редактирования
        if (process.env.NODE_ENV === 'development') {
          console.log('ItemCustomizationModal - Edit mode initialization:', {
            itemName: item.name,
            initialItem_full: initialItem, // Полный объект для отладки
            initialItem_customizations: initialItem.customizations,
            initialItem_customizations_addons: initialItem.customizations?.addons,
            initialItem_customizations_addons_length: initialItem.customizations?.addons?.length || 0,
            initialItem_size_id: initialItem.size_id,
            initialItem_addon_ids: initialItem.addon_ids,
            initialItem_addons: initialItem.addons,
            availableSizes: sizes.length,
            availableAddons: addons.length,
            availableAddonIds: addons.map(a => a.id)
          });
        }
        
        // Обработка размера
        let sizeId = null;
        if (initialItem.customizations?.sizeId) {
          sizeId = initialItem.customizations.sizeId;
        } else if (initialItem.size_id) {
          sizeId = initialItem.size_id;
        }
        // Проверяем, что размер все еще доступен в меню
        if (sizeId && sizes.length > 0) {
          const sizeExists = sizes.find(s => s.id === sizeId);
          if (!sizeExists) {
            // Если размер больше не доступен, показываем предупреждение и не устанавливаем автоматически другой размер
            console.warn(`Size ${sizeId} no longer available for item ${item.name}. User should select a new size.`);
            // Оставляем sizeId пустым, чтобы пользователь выбрал новый размер
            sizeId = null;
          }
        }
        setSelectedSizeId(sizeId);
        
        // Обработка аддонов
        let addonIds = [];
        
        // Проверяем customizations.addons (массив объектов с id)
        if (initialItem.customizations?.addons && Array.isArray(initialItem.customizations.addons)) {
          addonIds = initialItem.customizations.addons
            .map(a => (typeof a === 'object' && a !== null ? a.id : a))
            .filter(id => id != null && id !== undefined);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('Found addons in customizations.addons:', addonIds);
          }
        }
        // Проверяем addon_ids (может быть массив ID или объектов)
        else if (initialItem.addon_ids && Array.isArray(initialItem.addon_ids)) {
          addonIds = initialItem.addon_ids
            .map(id => (typeof id === 'object' && id !== null ? id.id : id))
            .filter(id => id != null && id !== undefined);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('Found addons in addon_ids:', addonIds);
          }
        }
        // Проверяем addons напрямую (поддержка и сгруппированного, и плоского формата)
        else if (initialItem.addons && Array.isArray(initialItem.addons)) {
          const flatAddons = getAddonsFlat(initialItem.addons);
          addonIds = flatAddons
            .map(a => (typeof a === 'object' && a !== null ? a.id : a))
            .filter(id => id != null && id !== undefined);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('Found addons in addons:', addonIds);
          }
        }
        
        // Фильтруем только те аддоны, которые доступны в текущем меню
        if (addons.length > 0) {
          const availableAddonIds = addons.map(a => a.id);
          const originalCount = addonIds.length;
          addonIds = addonIds.filter(id => availableAddonIds.includes(id));
          
          if (process.env.NODE_ENV === 'development') {
            console.log('Filtered addons (available in menu):', {
              original: originalCount,
              afterFilter: addonIds.length,
              addonIds
            });
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.warn('No addons available in menu for this item');
          }
        }
        
        setSelectedAddonIds(addonIds);
        setQuantity(initialItem.quantity || 1);
      } else {
        // Режим добавления: устанавливаем значения по умолчанию
        const defaultSize = sizes.find(s => s.is_default) || sizes[0];
        setSelectedSizeId(defaultSize?.id || null);
        setSelectedAddonIds([]);
        setQuantity(1);
      }
      // Выбор первой категории допников по умолчанию
      const groups = getAddonGroups(item?.addons || []);
      if (groups.length > 0) {
        setSelectedAddonCategory(groups[0].type);
      } else {
        setSelectedAddonCategory(null);
      }
    }
  }, [isOpen, item, sizes, addons, editMode, initialItem]);

  // Базовая цена: либо из размера, либо из самого товара
  const basePrice = useMemo(() => {
    if (!item) return 0;
    if (sizes.length > 0 && selectedSizeId) {
      const size = sizes.find(s => s.id === selectedSizeId);
      return size ? Number(size.price) : Number(item.price || 0);
    }
    return Number(item.price || 0);
  }, [sizes, selectedSizeId, item]);

  // Цена всех выбранных допников
  const addonsPrice = useMemo(() => {
    return selectedAddonIds.reduce((sum, addonId) => {
      const addon = addons.find(a => a.id === addonId);
      return sum + (addon ? Number(addon.price) : 0);
    }, 0);
  }, [addons, selectedAddonIds]);

  // Итоговая цена за все количество
  const totalPrice = (basePrice + addonsPrice) * quantity;

  const handleToggleAddon = (addonId) => {
    setSelectedAddonIds(prev =>
      prev.includes(addonId)
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const handleConfirm = () => {
    // Если есть размеры, но размер не выбран - не продолжаем
    if (sizes.length > 0 && !selectedSizeId) {
      return;
    }

    const chosenSize = sizes.find(s => s.id === selectedSizeId) || null;
    const chosenAddons = addons.filter(a => selectedAddonIds.includes(a.id));

    // Формируем объект для добавления/обновления в корзине
    const preparedItem = {
      ...item,
      quantity,
      price: basePrice + addonsPrice, // цена за одну порцию с учетом размера и допников
      size: chosenSize?.size_name || null,
      size_id: chosenSize?.id || null,
      customizations: {
        sizeId: chosenSize?.id || null,
        sizeName: chosenSize?.size_name || null,
        sizeVolumeWeight: chosenSize?.volume_weight || null,
        addons: chosenAddons.map(a => ({
          id: a.id,
          name: a.name,
          price: a.price,
          volume_weight: a.volume_weight,
        })),
      },
    };

    // В режиме редактирования вызываем onUpdate, иначе onConfirm
    if (editMode && onUpdate) {
      onUpdate(preparedItem);
    } else {
    onConfirm(preparedItem);
    }
  };

  // Проверка: можно ли подтвердить заказ
  const canConfirm = sizes.length === 0 || (sizes.length > 0 && selectedSizeId !== null);

  const hasCustomizations = sizes.length > 0 || addons.length > 0;

  // Ранний возврат после всех хуков
  if (!isOpen || !item) {
    return null;
  }

  const modalContent = (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)'
      }}
    >
      <div 
        className="modal-container item-customization-modal" 
        onClick={(e) => e.stopPropagation()}
        style={{ 
          position: 'relative',
          zIndex: 2001,
          background: 'white',
          borderRadius: '24px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          margin: '20px'
        }}
      >
        <div className="modal-header">
          <h2>{item.name}</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {!hasCustomizations ? (
            <div className="no-customizations">
              <p>Для этого товара доступна только настройка количества</p>
            </div>
          ) : (
            <>
              {/* Размеры */}
              {sizes.length > 0 && (
                <div className="customization-section">
                  <h3 className="section-title">Размер</h3>
                  <div className="size-list">
                    {sizes.map(size => {
                      const isSelected = selectedSizeId === size.id;
                      return (
                        <button
                          key={size.id}
                          className={`size-pill ${isSelected ? 'active' : ''}`}
                          onClick={() => setSelectedSizeId(size.id)}
                        >
                          <div className="size-name">{size.size_name}</div>
                          {size.volume_weight && (
                            <div className="size-volume">{size.volume_weight}</div>
                          )}
                          <div className="size-price">{Number(size.price).toFixed(2)} ₽</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Допники по категориям (сиропы / молоко / топпинг / урбеч / вкус) */}
              {addonGroups.length > 0 && (
                <div className="customization-section">
                  {addonGroups.length > 1 ? (
                    <>
                      <h3 className="section-title">Дополнительно</h3>
                      <div className="addon-category-tabs">
                        {addonGroups.map((group) => {
                          const isActive = selectedAddonCategory === group.type;
                          const label = getAddonTypeLabel(group.type);
                          return (
                            <button
                              key={group.type}
                              type="button"
                              className={`addon-category-tab ${isActive ? 'active' : ''}`}
                              onClick={() => setSelectedAddonCategory(group.type)}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      <div className="addons-list">
                        {currentCategoryPositions.map((addon) => {
                          const isSelected = selectedAddonIds.includes(addon.id);
                          return (
                            <label key={addon.id} className={`addon-row ${isSelected ? 'selected' : ''}`}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleAddon(addon.id)}
                              />
                              <div className="addon-info">
                                <span className="addon-name">{addon.name}</span>
                                {addon.volume_weight && (
                                  <span className="addon-volume">{addon.volume_weight}</span>
                                )}
                              </div>
                              <span className="addon-price">+ {Number(addon.price).toFixed(2)} ₽</span>
                            </label>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="section-title">
                        {getAddonTypeLabel(addonGroups[0].type)}
                      </h3>
                      <div className="addons-list">
                        {addonGroups[0].positions.map((addon) => {
                          const isSelected = selectedAddonIds.includes(addon.id);
                          return (
                            <label key={addon.id} className={`addon-row ${isSelected ? 'selected' : ''}`}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleAddon(addon.id)}
                              />
                              <div className="addon-info">
                                <span className="addon-name">{addon.name}</span>
                                {addon.volume_weight && (
                                  <span className="addon-volume">{addon.volume_weight}</span>
                                )}
                              </div>
                              <span className="addon-price">+ {Number(addon.price).toFixed(2)} ₽</span>
                            </label>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* Количество */}
          <div className="customization-section">
            <h3 className="section-title">Количество</h3>
            <div className="quantity-controls-modal">
              <button
                className="quantity-btn"
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
              >
                −
              </button>
              <span className="quantity">{quantity}</span>
              <button
                className="quantity-btn"
                onClick={() => setQuantity(q => q + 1)}
              >
                +
              </button>
            </div>
          </div>

          {/* Итоговая цена */}
          <div className="customization-total">
            <div className="total-label">Итого:</div>
            <div className="total-price">{totalPrice.toFixed(2)} ₽</div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Отмена
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {editMode ? 'Сохранить изменения' : 'Добавить в корзину'}
          </button>
        </div>
      </div>
    </div>
  );

  // Используем Portal для рендеринга модального окна в document.body
  // Это гарантирует, что модальное окно будет поверх всех элементов
  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalContent, document.body);
  }
  
  return modalContent;
};

export default ItemCustomizationModal;

